import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AdminPanelService } from '../../admin-panel.service';
import { BenchmarkPortfolio, BenchmarkResponse, BenchmarkCreateRequest } from '../../../monitoring/monitoring.model';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { CsvUploadComponent } from '../../../monitoring/components/csv-upload/csv-upload.component';
import { MainNavbarComponent } from 'src/app/core/components/main-navbar/main-navbar.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'app-benchmark-management',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MainNavbarComponent,
    MatTooltipModule,
    DragDropModule,
  ],
  templateUrl: './benchmark-management.component.html',
  styleUrl: './benchmark-management.component.css'
})
export class BenchmarkManagementComponent implements OnInit {
  benchmarks: BenchmarkPortfolio[] = [];
  displayedColumns: string[] = ['name', 'description', 'company_count', 'created_at', 'visibility', 'actions'];
  isLoading = false;
  showCreateDialog = false;
  createForm: FormGroup;
  selectedBenchmark: BenchmarkPortfolio | null = null;
  benchmarkCompanies: any[] = [];
  filteredCompanies: any[] = [];
  companySearch = '';

  // Edit name
  editingBenchmark: BenchmarkPortfolio | null = null;
  editForm: FormGroup;

  // Edit description
  editingDescriptionId: number | null = null;
  descriptionForm: FormGroup;

  // Reorder mode
  isReorderMode = false;
  originalBenchmarks: BenchmarkPortfolio[] = [];

  constructor(
    private adminPanelService: AdminPanelService,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private fb: FormBuilder
  ) {
    this.createForm = this.fb.group({
      benchmark_name: ['', [Validators.required, Validators.minLength(1)]]
    });
    this.editForm = this.fb.group({
      benchmark_name: ['', [Validators.required, Validators.minLength(1)]]
    });
    this.descriptionForm = this.fb.group({
      description: ['']
    });
  }

  ngOnInit() {
    this.loadBenchmarks();
  }

  goBackToAdminPanel() {
    this.router.navigate(['/admin-panel']);
  }

  loadBenchmarks() {
    this.isLoading = true;
    this.benchmarkCompanies = [];
    this.selectedBenchmark = null;
    this.adminPanelService.getAllBenchmarks().subscribe({
      next: (res: BenchmarkResponse) => {
        this.benchmarks = res.payload;
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.snackBar.open('Failed to load benchmarks', 'Close', { duration: 3000 });
        console.error('Error loading benchmarks:', error);  
      }
    });
  }

  onCreateBenchmark() {
    this.showCreateDialog = true;
  }

  cancelCreate() {
    this.showCreateDialog = false;
    this.createForm.reset();
  }

  submitCreate() {
    this.benchmarkCompanies = [];
    this.selectedBenchmark = null;
    if (this.createForm.valid) {
      const benchmarkData: BenchmarkCreateRequest = {
        benchmark_name: this.createForm.value.benchmark_name
      };

      this.adminPanelService.createBenchmark(benchmarkData).subscribe({
        next: (res: BenchmarkResponse) => {
          this.snackBar.open('Benchmark created successfully', 'Close', { duration: 3000 });
          this.showCreateDialog = false;
          this.createForm.reset();
          this.loadBenchmarks();
        },
        error: (error) => {
          const errorMsg = error.error?.message || 'Failed to create benchmark';
          this.snackBar.open(errorMsg, 'Close', { duration: 5000 });
        }
      });
    }
  }

  onUploadCompanies(benchmark: BenchmarkPortfolio) {
    // Reuse CSV upload component
    const dialogRef = this.dialog.open(CsvUploadComponent, {
      width: '1200px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      data: { 
        benchmark: benchmark,
        isBenchmark: true
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.companies?.length > 0) {
        this.uploadCompaniesToBenchmark(benchmark.benchmark_id, result.companies);
      }
    });
  }

  uploadCompaniesToBenchmark(benchmarkId: number, companies: any[]) {
    this.benchmarkCompanies = [];
    this.selectedBenchmark = null;
    this.isLoading = true;
    this.adminPanelService.uploadBenchmarkCompanies(benchmarkId, companies).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.snackBar.open(`Successfully added ${companies.length} companies to benchmark`, 'Close', { duration: 3000 });
        this.loadBenchmarks();
      },
      error: (error) => {
        this.isLoading = false;
        const errorMsg = error.error?.message || 'Failed to upload companies';
        this.snackBar.open(errorMsg, 'Close', { duration: 5000 });
      }
    });
  }

  onDeleteBenchmark(benchmark: BenchmarkPortfolio) {
    this.benchmarkCompanies = [];
    this.selectedBenchmark = null;
    ConfirmationDialogComponent.confirmDelete(this.dialog, benchmark.benchmark_name).subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.adminPanelService.deleteBenchmark(benchmark.benchmark_id).subscribe({
          next: () => {
            this.isLoading = false;
            this.snackBar.open('Benchmark deleted successfully', 'Close', { duration: 3000 });
            this.loadBenchmarks();
         
          },
          error: (error) => {
            this.isLoading = false;
            const errorMsg = error.error?.message || 'Failed to delete benchmark';
            this.snackBar.open(errorMsg, 'Close', { duration: 5000 });
         
          }
        });
      }
    });
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }
  loadingCompanies = false;
  onViewCompanies(benchmark: BenchmarkPortfolio | null) {
    this.loadingCompanies = true;
    this.selectedBenchmark = benchmark;
    this.companySearch = '';
    this.benchmarkCompanies = [];
    this.filteredCompanies = [];

  this.adminPanelService.getBenchmarkCompanies(benchmark!.benchmark_id).subscribe({
      next: (res: any) => {
        if (res.status === 204) {
          this.benchmarkCompanies = [];
          this.loadingCompanies = false;
          return;
        }
        this.benchmarkCompanies = res.body.payload || [];
        this.applyCompanyFilter();
        this.selectedBenchmark!.company_count = this.benchmarkCompanies.length;
        this.loadingCompanies = false;
      },
      error: (error) => {
        this.snackBar.open('Failed to load benchmark companies', 'Close', { duration: 3000 });
        console.error('Error loading benchmark companies:', error);
        this.loadingCompanies = false;
      
      }
    });
  }

  applyCompanyFilter() {
    if (!this.benchmarkCompanies) {
      this.filteredCompanies = [];
      return;
    }
    const term = (this.companySearch || '').toLowerCase().trim();
    if (!term) {
      this.filteredCompanies = this.benchmarkCompanies;
      return;
    }
    this.filteredCompanies = this.benchmarkCompanies.filter((c: any) => {
      const name = (c.entity_name || '').toLowerCase();
      const id = (c.entityid || '').toLowerCase();
      const isin = (c.isin || '').toLowerCase();
      return name.includes(term) || id.includes(term) || isin.includes(term);
    });
  }

  onDeleteCompany(company: any) {

    if (!this.selectedBenchmark || !company?.entityid) {
      return;
    }
    this.adminPanelService.deleteBenchmarkCompany(this.selectedBenchmark.benchmark_id, company.entityid).subscribe({
      next: (res) => {
        this.snackBar.open(res.message || 'Company removed from benchmark', 'Close', { duration: 3000 });
        // Refresh companies list locally
        this.benchmarkCompanies = this.benchmarkCompanies.filter((c: any) => c.entityid !== company.entityid);
        this.applyCompanyFilter();
        // Also refresh overall benchmark counts
        this.onViewCompanies(this.selectedBenchmark);
      },
      error: (error) => {
        const msg = error.error?.message || 'Failed to remove company from benchmark';
        this.snackBar.open(msg, 'Close', { duration: 4000 });
      }
    });
  }

  // Edit benchmark name
  onEditBenchmark(benchmark: BenchmarkPortfolio) {
    this.editingBenchmark = benchmark;
    this.editForm.patchValue({ benchmark_name: benchmark.benchmark_name });
  }

  cancelEdit() {
    this.editingBenchmark = null;
    this.editForm.reset();
  }

  submitEdit() {
    if (this.editForm.valid && this.editingBenchmark) {
      const newName = this.editForm.value.benchmark_name;
      this.adminPanelService.updateBenchmark(this.editingBenchmark.benchmark_id, { benchmark_name: newName }).subscribe({
        next: () => {
          this.snackBar.open('Benchmark name updated successfully', 'Close', { duration: 3000 });
          this.editingBenchmark!.benchmark_name = newName;
          this.editingBenchmark = null;
          this.editForm.reset();
        },
        error: (error) => {
          const errorMsg = error.error?.message || 'Failed to update benchmark name';
          this.snackBar.open(errorMsg, 'Close', { duration: 5000 });
        }
      });
    }
  }

  // Visibility toggle
  onToggleVisibility(benchmark: BenchmarkPortfolio) {
    const newVisibility = !benchmark.is_visible;
    this.adminPanelService.updateBenchmarkVisibility(benchmark.benchmark_id, newVisibility).subscribe({
      next: () => {
        benchmark.is_visible = newVisibility;
        this.snackBar.open(
          newVisibility ? 'Benchmark is now visible to users' : 'Benchmark is now hidden from users',
          'Close', { duration: 3000 }
        );
      },
      error: (error) => {
        const errorMsg = error.error?.message || 'Failed to update visibility';
        this.snackBar.open(errorMsg, 'Close', { duration: 5000 });
      }
    });
  }

  // Reorder mode
  enterReorderMode() {
    this.isReorderMode = true;
    this.originalBenchmarks = [...this.benchmarks];
    this.selectedBenchmark = null;
    this.benchmarkCompanies = [];
  }

  cancelReorder() {
    this.benchmarks = [...this.originalBenchmarks];
    this.isReorderMode = false;
  }

  onDrop(event: CdkDragDrop<BenchmarkPortfolio[]>) {
    moveItemInArray(this.benchmarks, event.previousIndex, event.currentIndex);
  }

  saveOrder() {
    const orderedIds = this.benchmarks.map(b => b.benchmark_id);
    this.isLoading = true;
    this.adminPanelService.reorderBenchmarks(orderedIds).subscribe({
      next: () => {
        this.isLoading = false;
        this.snackBar.open('Order saved successfully', 'Close', { duration: 3000 });
        this.isReorderMode = false;
      },
      error: (error) => {
        this.isLoading = false;
        const errorMsg = error.error?.message || 'Failed to save order';
        this.snackBar.open(errorMsg, 'Close', { duration: 5000 });
      }
    });
  }

  // Edit description
  onEditDescription(benchmark: BenchmarkPortfolio) {
    this.editingDescriptionId = benchmark.benchmark_id;
    this.descriptionForm.patchValue({ description: benchmark.description || '' });
  }

  cancelDescription() {
    this.editingDescriptionId = null;
    this.descriptionForm.reset();
  }

  submitDescription() {
    if (this.editingDescriptionId) {
      const description = this.descriptionForm.value.description;
      const benchmark = this.benchmarks.find(b => b.benchmark_id === this.editingDescriptionId);

      this.adminPanelService.updateBenchmarkDescription(this.editingDescriptionId, description).subscribe({
        next: () => {
          this.snackBar.open('Description updated successfully', 'Close', { duration: 3000 });
          if (benchmark) {
            benchmark.description = description;
          }
          this.editingDescriptionId = null;
          this.descriptionForm.reset();
        },
        error: (error) => {
          const errorMsg = error.error?.message || 'Failed to update description';
          this.snackBar.open(errorMsg, 'Close', { duration: 5000 });
        }
      });
    }
  }
}

